import { Button, Row } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import * as React from "react";
import { PaymentForm, CreditCard } from "react-square-web-payments-sdk";
import usePost from "../hooks/usePost";
import { openConfirmNotification, openErrorNotification } from "../utils/toast";
import { useState } from "react";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
const MyPaymentForm = ({
  amount,
  setPaymentModal,
  InvoiceNo,
  OrderName,
  refetch,
  showCardLayout,
  setShowCardLayout,
  onPaymentSuccess,
  beforePayment,
  DealerNo,
}) => {
  const [loading, setLoading] = useState(false);
  /** Default: Pay with Card (Square). False = manual / saved-card payment payload. */
  const [internalShowCardLayout, internalSetShowCardLayout] = useState(true);

  const isCardLayoutControlled =
    typeof showCardLayout === "boolean" &&
    typeof setShowCardLayout === "function";
  const [showCardComingSoonMessage] = useState(true);

  const effectiveShowCardLayout = isCardLayoutControlled
    ? showCardLayout
    : internalShowCardLayout;
  const effectiveSetShowCardLayout = isCardLayoutControlled
    ? setShowCardLayout
    : internalSetShowCardLayout;

  const getNumericAmount = (value) => {
    // Accepts `12.34`, `"12.34"`, or `"$12.34"` and returns a number.
    const raw = value ?? "";
    const num = Number(String(raw).replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(num) ? num : NaN;
  };

  const numericAmount = getNumericAmount(amount);
  const displayAmount = Number.isFinite(numericAmount)
    ? numericAmount.toFixed(2)
    : amount;
  const displayAmountWithCurrency = Number.isFinite(numericAmount)
    ? `$${numericAmount.toFixed(2)}`
    : amount;

  const postUrl = `${process.env.REACT_APP_API_URL}/api/payments/`;
  const {
    data: postData,
    loading: postLoading,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);

  React.useEffect(() => {
    refetch();
  }, [postData, postLoading]);

  const handleSuccess = async (token) => {
    try {
      setLoading(true);
      const safeAmount = getNumericAmount(amount);
      const payload = {
        dealer_id: DealerNo,
        source_id: token?.token,
        amount: safeAmount,
      };

      const okToPay = beforePayment ? await beforePayment() : true;
      if (!okToPay) {
        setLoading(false);
        return;
      }

      postAction(payload);
    } catch (e) {
      setLoading(false);
    }
  };

  const handlePayWithoutNewCard = async () => {
    try {
      const safeAmount = getNumericAmount(amount);
      setLoading(true);

      const okToPay = beforePayment ? await beforePayment() : true;
      if (!okToPay) {
        setLoading(false);
        return;
      }

      postAction({
        dealer_id: DealerNo,
        amount: safeAmount,
        payment_mode: "MANUAL",
      });
    } catch (e) {
      setLoading(false);
    }
  };

  const handleError = (errors) => {
    console.error("Payment failed:", errors);
  };
  React.useEffect(() => {
    if (postError) {
      setLoading(false);
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong",
      );
    } else if (!postLoading && !postError && postData) {
      setLoading(false);
      onPaymentSuccess?.(postData);

      openConfirmNotification("Success");
      setPaymentModal(false);
    }
  }, [postError, postData, postLoading]);

  const paymentHelperText = effectiveShowCardLayout
    ? `Enter your card details to pay ${displayAmountWithCurrency}.`
    : `Complete payment of ${displayAmountWithCurrency} manually.`;
  const cardComingSoonMessage =
    "We are preparing a card payment system.\nPlease contact 857-346-0130 for Prepurchased License.";

  return (
    <>
      <style>{`#card-container button{display:none !important;}`}</style>
      {!effectiveShowCardLayout && (
        <p className="text-sm font-[Poppins] px-2 mb-4">{paymentHelperText}</p>
      )}

      {effectiveShowCardLayout && (
        <>
          {showCardComingSoonMessage ? (
            <div className="px-2">
              <Row>
                <div className="w-full rounded-md border border-[#91d5ff] bg-[#e6f7ff] px-4 py-3 text-center">
                  <p className="text-sm font-[Poppins] text-[#003a8c] m-0 whitespace-pre-line">
                    {cardComingSoonMessage}
                  </p>
                </div>
              </Row>
            </div>
          ) : (
            <div>
              <PaymentForm
                applicationId={process.env.REACT_APP_APPLICATION_ID}
                locationId={process.env.REACT_APP_LOCATION_ID}
                cardTokenizeResponseReceived={(token, buyer) => {
                  handleSuccess(token);
                }}
              >
                {/* Square's CreditCard Component for Tokenization */}
                <Row>
                  <div className="w-full justify-center items-center px-9 ">
                    <div id="card-container" className="space-y-2">
                      <CreditCard
                        buttonProps={{
                          id: "square-pay-button",
                          css: {
                            backgroundColor: "var(--text-color)",
                            fontSize: "14px",
                            padding: "4px 4px",
                            height: "32px",
                            width: "120px",
                            borderRadius: "4px",
                            margin: "0 auto",
                          },
                        }}
                      />
                    </div>
                  </div>
                </Row>
              </PaymentForm>
            </div>
          )}
        </>
      )}

      <div className="flex justify-center space-x-2 mt-2.5">
        <Button
          type="primary"
          className="min-w-[104px] h-[30px] py-1 px-[10px] font-medium flex items-center"
          loading={loading}
          disabled={effectiveShowCardLayout && showCardComingSoonMessage}
          onClick={() => {
            if (!effectiveShowCardLayout) {
              handlePayWithoutNewCard();
              return;
            }

            if (showCardComingSoonMessage) {
              return;
            }

            const btn =
              document.getElementById("square-pay-button") ||
              document.querySelector("#card-container button");
            btn?.click();
          }}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          <span>Pay</span>
        </Button>
        <Button
          className="reset-button"
          onClick={() => setPaymentModal(false)}
          disabled={loading}
        >
          <CloseOutlined />
          <span>Cancel</span>
        </Button>
      </div>
    </>
  );
};

export default MyPaymentForm;
